/** Site comparison tab - ranks sites by waste cost/quantity/%, groups similar sites (site
 * group / region as a proxy peer group), shows best/worst performers, normalises by sales,
 * covers and purchase volume, flags unusual changes and shows performance against target. */

import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../components/ui/Section';
import { applyRoundAndFormat } from '../../../utils';
import { SiteWasteRow } from '../types';
import { PerformanceBandChip, StatusChip } from './StatusChip';

interface SiteComparisonTabProps {
  sites: SiteWasteRow[];
  currencyCode?: string;
}

const SiteComparisonTab = ({ sites, currencyCode }: SiteComparisonTabProps) => {
  const { t } = useTranslation('common');
  const rankedByCost = [...sites].sort((a, b) => b.wasteCost - a.wasteCost);
  const best = [...sites].sort((a, b) => a.wastePercentOfSales - b.wastePercentOfSales)[0];
  const worst = rankedByCost[0];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardSiteRankingTitle', 'Sites ranked by waste cost (normalised by sales, covers and purchases)')}
          </Typography>
          <Box sx={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={rankedByCost}
                layout="vertical"
                margin={{ left: 130 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category"
                  dataKey="site"
                  width={140} />
                <RechartsTooltip formatter={(value: number) => applyRoundAndFormat(value, currencyCode)} />
                <Bar dataKey="wasteCost"
                  name={t('wasteDashboardWasteCost', 'Waste cost')}
                  fill="#F79009" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Section>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        <Section>
          <Box sx={{ p: 4 }}>
            <StatusChip status="good"
              label={t('wasteDashboardBestPerformer', 'Best performer')} />
            <Typography variant="textLG"
              fontWeight={600}
              sx={{ mt: 1 }}>{best.site}</Typography>
            <Typography variant="textSM"
              color="text.secondary">
              {best.wastePercentOfSales.toFixed(1)}% {t('wasteDashboardOfSales', 'of sales')} \u00b7 {best.siteGroup} \u00b7 {best.region}
            </Typography>
          </Box>
        </Section>
        <Section>
          <Box sx={{ p: 4 }}>
            <StatusChip status="critical"
              label={t('wasteDashboardWorstPerformer', 'Worst performer')} />
            <Typography variant="textLG"
              fontWeight={600}
              sx={{ mt: 1 }}>{worst.site}</Typography>
            <Typography variant="textSM"
              color="text.secondary">
              {applyRoundAndFormat(worst.wasteCost, currencyCode)} {t('wasteDashboardWasteCost', 'waste cost')} \u00b7 {worst.siteGroup} \u00b7 {worst.region}
            </Typography>
          </Box>
        </Section>
      </Box>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardSiteComparisonTable', 'Site comparison detail')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('wasteDashboardSite', 'Site')}</TableCell>
                <TableCell>{t('wasteDashboardSiteGroup', 'Site group (peer group)')}</TableCell>
                <TableCell align="right">{t('wasteDashboardWasteCost', 'Waste cost')}</TableCell>
                <TableCell align="right">{t('wasteDashboardWastePerCover', 'Waste per cover')}</TableCell>
                <TableCell align="right">{t('wasteDashboardPercentOfSales', '% of sales')}</TableCell>
                <TableCell align="right">{t('wasteDashboardTargetPercent', 'Target %')}</TableCell>
                <TableCell align="right">{t('wasteDashboardTrend', 'Trend vs prior period')}</TableCell>
                <TableCell>{t('wasteDashboardPerformanceBand', 'Performance band')}</TableCell>
                <TableCell>{t('wasteDashboardAnomaly', 'Anomaly')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rankedByCost.map((row) => {
                const isAnomaly = Math.abs(row.trendVsPriorPeriod) > 10;
                return (
                  <TableRow key={row.site}>
                    <TableCell>{row.site}</TableCell>
                    <TableCell>{row.siteGroup}</TableCell>
                    <TableCell align="right">{applyRoundAndFormat(row.wasteCost, currencyCode)}</TableCell>
                    <TableCell align="right">{applyRoundAndFormat(row.wastePerCover, currencyCode)}</TableCell>
                    <TableCell align="right">{row.wastePercentOfSales.toFixed(1)}%</TableCell>
                    <TableCell align="right">{row.targetWastePercent.toFixed(1)}%</TableCell>
                    <TableCell align="right">
                      {row.trendVsPriorPeriod > 0 ? '\u2191' : '\u2193'} {Math.abs(row.trendVsPriorPeriod).toFixed(1)}%
                    </TableCell>
                    <TableCell><PerformanceBandChip band={row.performanceBand} /></TableCell>
                    <TableCell>
                      {isAnomaly ? <StatusChip status="critical"
                        label={t('wasteDashboardUnusualChange', 'Unusual change')} /> : '\u2013'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Section>
    </Box>
  );
};

export default SiteComparisonTab;
